import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useMutation } from "@apollo/client";
import { useModalConfirmation, useQueryData, useToast } from "hooks";
import { GET_INTERVIEWS } from "graphql/queries";
import { HIREFLIX_INVITE_CANDIDATE } from "graphql/mutations";
import { Col } from "components/Containers";
import { PrimaryLink } from "components/Links";

/**
 * useCandidateProfileInterview
 *
 * @params {String}   profileId
 * @params {Boolean}  isAdmin
 * @params {Object}   filters
 */
export default function useCandidateProfileInterview({ profileId, isAdmin, filters }) {
  const { show } = useModalConfirmation();
  const { toast } = useToast();
  const STATUS_COMPLETE = "completed";
  const [filteredData, setFilteredData] = useState();
  const [isComplete, setIsComplete] = useState(false);
  const [getInterviewUrl, { data: interviewData, loading: loadingInterviewUrl }] =
    useMutation(HIREFLIX_INVITE_CANDIDATE);
  const { data, loading } = useQueryData({
    queryName: GET_INTERVIEWS,
    keyName: "interviews",
    variables: {
      profileId,
      filters,
    },
  });
  const unfilteredData = data?.interviews?.nodes;

  useEffect(() => {
    const completedInterview = unfilteredData?.filter((item) => item?.interviewResponse?.completed);
    const isComplete = completedInterview && completedInterview.length > 0;

    setIsComplete(isComplete);
  }, [unfilteredData]);

  // For Admins, only show data that has a response
  useEffect(() => {
    setFilteredData(
      isAdmin
        ? unfilteredData?.filter(
            (interview) => interview?.interviewResponse && interview?.interviewResponse?.status === STATUS_COMPLETE
          )
        : unfilteredData
    );
  }, [unfilteredData, isAdmin]);

  const handleInterviewLink = useCallback(
    async (item) => {
      const { id: interviewId, interviewResponse } = item;
      const publicUrl =
        interviewResponse?.publicUrl ?? interviewData?.addInterviewResponse?.interviewResponse?.publicUrl;

      if (isAdmin) {
        window.location = interviewResponse?.privateUrl;
        return;
      }

      if (publicUrl) {
        await showInterviewModal(publicUrl);
        return;
      }

      await getInterviewUrl({
        variables: {
          input: {
            profileId,
            enableNotifications: false,
            params: { interviewId },
          },
        },
        onError: () => {
          toast.error(<FormattedMessage id="Candidate.CandidateInterview.Error" />);
        },
      }).then(async (d) => {
        const fetchedUrl = d?.data?.addInterviewResponse?.interviewResponse?.publicUrl;
        if (fetchedUrl) await showInterviewModal(fetchedUrl);
      });
    },
    [interviewData, profileId, isAdmin, show, getInterviewUrl]
  );

  const showInterviewModal = (url) =>
    show(
      <FormattedMessage id="useCandidateProfileInterview.Title" />,
      <Col gap="1rem">
        <b>
          <FormattedMessage id="useCandidateProfileInterview.Body1" />
        </b>
        <FormattedMessage
          id="useCandidateProfileInterview.Body2"
          values={{
            link: (
              <PrimaryLink to={url} withMoreIcon>
                <FormattedMessage id="useCandidateProfileInterview.TestUrl" />
              </PrimaryLink>
            ),
          }}
        />
      </Col>,
      {
        secondaryButtonMessageId: "Global.Close",
        showSave: false,
      }
    );

  return {
    data: filteredData,
    loading,
    isComplete,
    handleInterviewLink,
    loadingInterviewUrl,
    STATUS_COMPLETE,
  };
}
